<template>
    <BaseCard class="flex flex-col gap-5 overflow-hidden text-ink-muted">
        <!-- New insights state -->
        <template v-if="cardState === 'new_insights' && insightSummary">
            <div class="flex flex-col flex-1 min-h-0 gap-2">
                <div class="flex items-center gap-2 text-ink">
                    <i class="bi bi-stars text-xs shrink-0" aria-hidden="true"></i>
                    <p class="details">{{ t("myWork.homepageCard.newInsightBanner") }}</p>
                </div>
                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <MarkdownText :text="insightSummary" class="body prose max-w-none text-ink prose-headings:my-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0" />
                    <div class="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-surface-raised to-transparent" aria-hidden="true"></div>
                </div>
            </div>
            <div>
                <BaseButton theme="neutral" class="w-full" @click="handleClick">
                    {{ insightCount === 1 ? t("myWork.homepageCard.seeNewInsight") : t("myWork.homepageCard.seeNewInsights", { count: insightCount ?? "" }).trim() }}
                </BaseButton>
            </div>
        </template>

        <!-- Activation state -->
        <template v-else-if="cardState === 'activation'">
            <div class="flex flex-col gap-3 flex-1">
                <div class="nadia-subtitle text-ink">{{ t("myWork.homepageCard.title") }}</div>
                <div class="body-small text-ink-muted mt-1">{{ t("myWork.homepageCard.subtitle") }}</div>
                <ul v-if="activationItems?.length" class="flex flex-col gap-2.5 mt-1">
                    <li v-for="item in activationItems" :key="item.label" class="flex items-center gap-2.5 body-small" :class="item.complete ? 'text-ink-muted' : 'text-ink'">
                        <span class="flex size-6 shrink-0 items-center justify-center" aria-hidden="true">
                            <SetupCalendarTile v-if="item.tier === 1" class="size-6" />
                            <SetupPerspectiveTile v-else-if="item.tier === 2" class="size-6" />
                            <SetupProfileTile v-else-if="item.tier === 3" class="size-6" />
                            <CalendarStarIcon v-else-if="item.tier === 4" size="sm" />
                        </span>
                        <span :class="{ 'line-through': item.complete }">{{ item.label }}</span>
                        <span class="sr-only">{{ item.complete ? t("myWork.homepageCard.itemCompleted") : t("myWork.homepageCard.itemNotCompleted") }}</span>
                    </li>
                </ul>
            </div>
            <div class="mt-auto">
                <BaseButton theme="neutral" class="w-full" @click="handleClick">{{ t("myWork.homepageCard.unlockButton") }}</BaseButton>
            </div>
        </template>
    </BaseCard>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import CalendarStarIcon from "~vue/icons/CalendarStarIcon.vue";
import SetupCalendarTile from "~vue/icons/SetupCalendarTile.vue";
import SetupPerspectiveTile from "~vue/icons/SetupPerspectiveTile.vue";
import SetupProfileTile from "~vue/icons/SetupProfileTile.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import type { MyWorkCardInfo } from "~vue/types/chat_home";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        cardState: "new_insights" | "activation";
        cardInfo?: MyWorkCardInfo | null;
    }>(),
    { cardInfo: null },
);

const insightSummary = computed(() => (props.cardInfo?.kind === "new_insights" ? props.cardInfo.summary : null));
const insightCount = computed(() => (props.cardInfo?.kind === "new_insights" ? props.cardInfo.count : null));
const activationItems = computed(() => (props.cardInfo?.kind === "activation" ? props.cardInfo.items : null));

onMounted(() => {
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CARD_SHOWN, {
        card_state: props.cardState,
        insight_count: insightCount.value ?? null,
    });
});

const handleClick = () => {
    logUserInteraction(AmplitudeEventNames.MYWORK_HOMEPAGE_CARD_CLICKED, {
        card_state: props.cardState,
        insight_count: insightCount.value ?? null,
    });
    router.visit("/intelligence/my-work/");
};
</script>
